import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users, CheckCircle2, Wallet,
  Plus, Upload, Download, Pencil, Trash2, MessageSquarePlus, Eye, UserCheck, Search, ChevronDown,
} from '@/components/icons';
import {
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, LabelList, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { Table } from '@/components/ui/Table';
import { Pagination } from '@/components/ui/Pagination';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { listUsers } from '@/api/admin';
import {
  listLeads, createLead, updateLead, deleteLead as apiDeleteLead, addLeadComm,
  listTeam, addTeamMember, deleteTeamMember, leadsExportPath,
  type LeadPayload, type CommPayload, type TeamMember,
} from '@/api/leads';
import { downloadExcel } from '@/lib/downloadExcel';
import { formatINR } from '@/lib/format';
import { LeadFormModal } from './LeadFormModal';
import { CommLogModal } from './CommLogModal';
import { LeadViewModal } from './LeadViewModal';
import { ConvertLeadModal } from './ConvertLeadModal';
import {
  LEAD_CATEGORIES, LEAD_SOURCES, LEAD_STATUSES,
  filterByRange, computeStats, categoryBreakdown, personStats, bySource, byMonth, accountsRollup,
  firstContact, lastUpdate, commCount,
  type Lead, type LeadComm, type LeadStatus, type RangeKey, type AccountRow,
} from './leadDomain';

const STATUS_TONE: Record<LeadStatus, 'success' | 'danger' | 'warning' | 'info' | 'accent' | 'muted'> = {
  Open: 'muted', Contacted: 'info', 'Sampling/Design': 'warning', Negotiation: 'warning', Converted: 'success', Lost: 'danger',
};
const RANGES: [RangeKey, string][] = [['week', 'This week'], ['month', 'This month'], ['year', 'This year'], ['all', 'All time']];
const TABS = [['overview', 'Overview'], ['leads', 'Leads'], ['team', 'Team'], ['accounts', 'Accounts']] as const;
type TabKey = (typeof TABS)[number][0];

// Small themed panel + table primitives (shared look with the SIS dashboard)
function Panel({ title, subtitle, action, children }: { title: string; subtitle?: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)]">
      <div className="flex items-start justify-between gap-3 px-5 pb-3 pt-4">
        <div>
          <h3 className="text-sm font-extrabold text-[var(--text-primary)]">{title}</h3>
          {subtitle && <p className="mt-0.5 text-[11px] text-[var(--text-muted)]">{subtitle}</p>}
        </div>
        {action}
      </div>
      <div className="px-5 pb-5">{children}</div>
    </section>
  );
}
const Empty = ({ children }: { children: ReactNode }) => (
  <div className="py-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>{children}</div>
);

const chartTooltip = { background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 12, color: 'var(--text-primary)' };

export function LeadDashboardPage() {
  const toast = useToast();
  const qc = useQueryClient();
  const leadsQuery = useQuery({ queryKey: ['leads'], queryFn: listLeads });
  const leads = leadsQuery.data ?? [];
  const teamQuery = useQuery({ queryKey: ['lead-team'], queryFn: listTeam });
  const team = useMemo<TeamMember[]>(() => teamQuery.data ?? [], [teamQuery.data]);
  const people = useMemo(() => team.map((t) => t.name), [team]);
  const [tab, setTab] = useState<TabKey>('overview');
  const [range, setRange] = useState<RangeKey>('all');

  // Leads-tab filters
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('All');
  const [srcFilter, setSrcFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');

  // modals
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Lead | null>(null);
  const [commLead, setCommLead] = useState<Lead | null>(null);
  const [viewing, setViewing] = useState<Lead | null>(null);
  const [converting, setConverting] = useState<Lead | null>(null);
  const [newPerson, setNewPerson] = useState('');

  const filtered = useMemo(() => filterByRange(leads, range), [leads, range]);

  // Team members are sourced from User Management — active Sales Managers and
  // Sales Executives. The Team tab's dropdown fetches from here.
  const usersQuery = useQuery({ queryKey: ['users'], queryFn: listUsers, staleTime: 60_000 });
  const salesStaff = useMemo(
    () =>
      (usersQuery.data ?? [])
        .filter((u) => (u.roleCode === 'SALES_MGR' || u.roleCode === 'SALES_EXEC') && u.status === 'Active')
        .map((u) => ({ name: u.fullName, role: u.role })),
    [usersQuery.data],
  );

  // ---- mutations (backed by the /leads API — Party records + lead_comm) ----
  const invalidate = () => qc.invalidateQueries({ queryKey: ['leads'] });
  const onErr = (e: unknown) => toast.push(apiError(e, 'Something went wrong'), 'error');
  const createMut = useMutation({ mutationFn: createLead, onSuccess: () => { invalidate(); toast.push('Lead saved', 'success'); }, onError: onErr });
  const updateMut = useMutation({ mutationFn: ({ id, payload }: { id: number; payload: LeadPayload }) => updateLead(id, payload), onSuccess: () => { invalidate(); toast.push('Lead updated', 'success'); }, onError: onErr });
  const deleteMut = useMutation({ mutationFn: apiDeleteLead, onSuccess: () => { invalidate(); toast.push('Lead deleted', 'success'); }, onError: onErr });
  const commMut = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: CommPayload }) => addLeadComm(id, payload),
    // Refresh the open modal from the returned lead so its comm list updates live.
    onSuccess: (updated) => { invalidate(); setCommLead((cur) => (cur && cur.id === updated.id ? updated : cur)); toast.push('Communication logged', 'success'); },
    onError: onErr,
  });
  const invalidateTeam = () => qc.invalidateQueries({ queryKey: ['lead-team'] });
  const addTeamMut = useMutation({ mutationFn: addTeamMember, onSuccess: () => { invalidateTeam(); toast.push('Team member saved', 'success'); }, onError: onErr });
  const deleteTeamMut = useMutation({ mutationFn: deleteTeamMember, onSuccess: () => { invalidateTeam(); toast.push('Team member removed', 'success'); }, onError: onErr });

  function toPayload(lead: Lead): LeadPayload {
    return {
      customer: lead.customer, category: lead.category, source: lead.source, referralName: lead.referralName,
      assignedTo: lead.assignedTo, contactNumber: lead.contactNumber, email: lead.email, status: lead.status,
      samplingCostInr: lead.samplingCostInr, valueInr: lead.valueInr, notes: lead.notes, date: lead.date, nextFollowUp: lead.nextFollowUp,
    };
  }
  // New leads carry a client-generated (non-numeric) id; existing ones a numeric
  // Party id — that tells create from update.
  function saveLead(lead: Lead) {
    if (/^\d+$/.test(lead.id)) updateMut.mutate({ id: Number(lead.id), payload: toPayload(lead) });
    else createMut.mutate(toPayload(lead));
  }
  function deleteLead(id: string) { deleteMut.mutate(Number(id)); }
  function addComm(leadId: string, comm: LeadComm) {
    commMut.mutate({ id: Number(leadId), payload: { date: comm.date, type: comm.type, by: comm.by, note: comm.note } });
  }
  function addPerson() {
    const name = newPerson.trim();
    if (name && !people.includes(name)) { addTeamMut.mutate(name); setNewPerson(''); }
  }
  function removePerson(id: number) { deleteTeamMut.mutate(id); }

  return (
    <div>
      <SectionHeader title="Lead Dashboard" />

      {/* Tabs + period */}
      <div className="mb-5 flex flex-wrap items-end justify-between gap-x-4 gap-y-3 border-b border-[var(--border-color)]">
        <nav className="-mb-px flex gap-1 overflow-x-auto">
          {TABS.map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setTab(k)}
              className={`whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-bold transition ${
                tab === k ? 'border-accent text-accent' : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
        <div className="mb-2"><Pills options={RANGES} value={range} onChange={(v) => setRange(v as RangeKey)} /></div>
      </div>

      {tab === 'overview' && <Overview leads={filtered} people={people} rangeLabel={RANGES.find(([k]) => k === range)?.[1] ?? ''} />}
      {tab === 'leads' && (
        <LeadsTab
          leads={filtered} search={search} setSearch={setSearch}
          catFilter={catFilter} setCatFilter={setCatFilter}
          srcFilter={srcFilter} setSrcFilter={setSrcFilter}
          statusFilter={statusFilter} setStatusFilter={setStatusFilter}
          onNew={() => { setEditing(null); setFormOpen(true); }}
          onView={(l) => setViewing(l)}
          onEdit={(l) => { setEditing(l); setFormOpen(true); }}
          onDelete={deleteLead}
          onLog={(l) => setCommLead(l)}
          onConvert={(l) => setConverting(l)}
          onExport={async () => {
            // 5-min timeout so a large, uncapped export never times out.
            try { await downloadExcel(leadsExportPath(), 'GRHAMOY_Leads.xlsx', 5 * 60 * 1000); }
            catch (e) { toast.push(apiError(e, 'Export failed'), 'error'); }
          }}
          onImport={() => toast.push('Excel import will be enabled once the Lead API is connected.', 'info')}
        />
      )}
      {tab === 'team' && (
        <TeamTab leads={filtered} team={team} salesStaff={salesStaff} loadingStaff={usersQuery.isLoading} newPerson={newPerson} setNewPerson={setNewPerson} addPerson={addPerson} onDelete={removePerson} />
      )}
      {tab === 'accounts' && <AccountsTab leads={filtered} />}

      <LeadFormModal open={formOpen} editing={editing} people={people} onClose={() => setFormOpen(false)} onSave={saveLead} />
      <CommLogModal open={commLead !== null} lead={commLead} people={people} onClose={() => setCommLead(null)} onAdd={addComm} />
      <LeadViewModal open={viewing !== null} lead={viewing} onClose={() => setViewing(null)} />
      <ConvertLeadModal open={converting !== null} lead={converting} onClose={() => setConverting(null)} onConverted={invalidate} />
    </div>
  );
}

function Pills({ options, value, onChange }: { options: readonly (readonly [string, string])[]; value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex w-fit gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] p-1">
      {options.map(([k, label]) => (
        <button
          key={k}
          onClick={() => onChange(k)}
          className={`whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs font-bold transition sm:px-3.5 ${
            value === k ? 'bg-accent text-white shadow-sm' : 'text-[var(--text-secondary)] hover:text-accent'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

// ---------- Overview ----------
// Status colours used across the overview (bars, dots, figures).
// Pending is blue (in progress) so it never reads as the brand orange used for volumes.
const STATUS_COLOR = { pending: '#3b82f6', converted: '#22c55e', lost: '#ef4444' } as const;
const pct = (n: number, of: number) => (of ? Math.round((n / of) * 100) : 0);
// One fixed colour per lead source (by its place in LEAD_SOURCES), so a source
// keeps its colour whatever the period or ranking.
const SOURCE_COLORS = ['#FF8C00', '#6366f1', '#14b8a6', '#ec4899', '#0ea5e9', '#a855f7', '#84cc16', '#64748b'];
const sourceColor = (name: string) => SOURCE_COLORS[Math.max(0, (LEAD_SOURCES as readonly string[]).indexOf(name)) % SOURCE_COLORS.length];
/** "2026-07" → "Jul 26" for the month chart. */
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function monthLabel(ym: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(ym);
  return m ? `${MONTH_SHORT[Number(m[2]) - 1]} ${m[1].slice(2)}` : ym;
}

/** Pending / converted / lost as one horizontal stacked bar. Empty → a plain track. */
function StatusBar({ pending, converted, lost, height = 8 }: { pending: number; converted: number; lost: number; height?: number }) {
  const total = pending + converted + lost;
  return (
    <div className="flex w-full overflow-hidden rounded-full bg-[var(--bg-secondary)]" style={{ height, gap: total ? 2 : 0 }}>
      {total > 0 && ([['pending', pending], ['converted', converted], ['lost', lost]] as const).map(([k, v]) => v > 0 && (
        <div key={k} title={`${k[0].toUpperCase()}${k.slice(1)}: ${v}`} style={{ width: `${(v / total) * 100}%`, background: STATUS_COLOR[k] }} />
      ))}
    </div>
  );
}
function Dot({ color }: { color: string }) {
  return <span className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ background: color }} />;
}

function Overview({ leads, people, rangeLabel }: { leads: Lead[]; people: string[]; rangeLabel: string }) {
  const stats = computeStats(leads);
  const cats = categoryBreakdown(leads);
  const sources = bySource(leads).sort((a, b) => b.value - a.value);
  const months = byMonth(leads);
  const team = people.map((p) => personStats(leads, p));

  const statusParts = [
    { key: 'pending', label: 'Pending', value: stats.pendingCount, color: STATUS_COLOR.pending },
    { key: 'converted', label: 'Converted', value: stats.convertedCount, color: STATUS_COLOR.converted },
    { key: 'lost', label: 'Lost', value: stats.lostCount, color: STATUS_COLOR.lost },
  ];

  return (
    <div className="space-y-5">
      {/* Summary — total, how it splits by status, and what has closed. */}
      <section className="grid overflow-hidden rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] md:grid-cols-[minmax(150px,auto)_1fr_minmax(200px,auto)]">
        <div className="flex items-center gap-3 border-b border-[var(--border-color)] p-5 md:block md:border-b-0 md:border-r">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent md:mb-3"><Users size={18} /></span>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">Total leads</div>
            <div className="text-3xl font-extrabold tabular-nums text-[var(--text-primary)]">{stats.total}</div>
            <div className="text-[11px] text-[var(--text-muted)]">{rangeLabel}</div>
          </div>
        </div>

        <div className="border-b border-[var(--border-color)] p-5 md:border-b-0 md:border-r">
          <div className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">Where the leads stand</div>
          <StatusBar pending={stats.pendingCount} converted={stats.convertedCount} lost={stats.lostCount} height={12} />
          <div className="mt-4 grid grid-cols-3 gap-3">
            {statusParts.map((s) => (
              <div key={s.key}>
                <div className="flex items-center gap-1.5 text-xs font-semibold text-[var(--text-secondary)]"><Dot color={s.color} />{s.label}</div>
                <div className="mt-1 text-xl font-extrabold tabular-nums" style={{ color: s.value ? s.color : 'var(--text-muted)' }}>{s.value}</div>
                <div className="text-[11px] tabular-nums text-[var(--text-muted)]">{pct(s.value, stats.total)}% of leads</div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 p-5 md:grid-cols-1">
          <div>
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)]"><Wallet size={13} /> Order value closed</div>
            <div className="mt-1 text-2xl font-extrabold tabular-nums" style={{ color: stats.totalValue ? STATUS_COLOR.converted : 'var(--text-primary)' }}>₹{formatINR(stats.totalValue) || 0}</div>
          </div>
          <div>
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)]"><CheckCircle2 size={13} /> Conversion rate</div>
            <div className="mt-1 text-2xl font-extrabold tabular-nums text-[var(--text-primary)]">{stats.conversionRate}%</div>
          </div>
        </div>
      </section>

      <div className="grid gap-5 md:grid-cols-2">
        <Panel title="Leads by source">
          {sources.length === 0 ? <Empty>No leads in this period</Empty> : (
            <div className="flex flex-col items-center gap-5 sm:flex-row">
              {/* Donut with the period total in the middle. */}
              <div className="relative h-[180px] w-[180px] shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={sources} dataKey="value" nameKey="name" innerRadius={58} outerRadius={84} paddingAngle={sources.length > 1 ? 3 : 0} cornerRadius={sources.length > 1 ? 4 : 0} stroke="none" startAngle={90} endAngle={-270}>
                      {sources.map((s) => <Cell key={s.name} fill={sourceColor(s.name)} />)}
                    </Pie>
                    <Tooltip contentStyle={chartTooltip} formatter={(v, n) => [`${v} (${pct(Number(v), stats.total)}%)`, n]} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-2xl font-extrabold tabular-nums text-[var(--text-primary)]">{stats.total}</span>
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">leads</span>
                </div>
              </div>
              <ul className="w-full min-w-0 flex-1 space-y-2.5">
                {sources.map((s) => (
                  <li key={s.name} className="flex items-center gap-2.5 text-xs">
                    <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: sourceColor(s.name) }} />
                    <span className="min-w-0 flex-1 truncate font-semibold text-[var(--text-primary)]">{s.name}</span>
                    <span className="font-extrabold tabular-nums text-[var(--text-primary)]">{s.value}</span>
                    <span className="w-10 text-right tabular-nums text-[var(--text-muted)]">{pct(s.value, stats.total)}%</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Panel>

        <Panel title="Leads by month">
          {months.length === 0 ? <Empty>No leads in this period</Empty> : (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={months} margin={{ top: 22, right: 16, left: -22, bottom: 0 }}>
                <defs>
                  <linearGradient id="leadMonthFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF8C00" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#FF8C00" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(128,128,128,0.15)" vertical={false} />
                <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} padding={{ left: 12, right: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={chartTooltip} cursor={{ stroke: 'var(--border-color)' }} labelFormatter={(m) => monthLabel(String(m))} />
                <Area
                  type="monotone" dataKey="inquiries" name="Leads" stroke="#FF8C00" strokeWidth={2.5} fill="url(#leadMonthFill)"
                  dot={{ r: 4, fill: '#FF8C00', stroke: 'var(--card-bg)', strokeWidth: 2 }} activeDot={{ r: 6, stroke: 'var(--card-bg)', strokeWidth: 2 }}
                >
                  <LabelList dataKey="inquiries" position="top" offset={10} style={{ fontSize: 10, fontWeight: 700, fill: 'var(--text-secondary)' }} />
                </Area>
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Panel>
      </div>

      <Panel title="Pending & conversion by category">
        {leads.length === 0 ? <Empty>No leads in this period</Empty> : (
          <div className="-mx-5 overflow-x-auto">
            <table className="w-full min-w-[520px] text-sm">
              <thead>
                <tr className="border-y border-[var(--border-color)] bg-[var(--bg-secondary)] text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)]">
                  <th className="px-5 py-2.5 text-left">Category</th>
                  <th className="px-3 py-2.5 text-right">Total</th>
                  <th className="px-3 py-2.5 text-right"><span className="inline-flex items-center gap-1.5"><Dot color={STATUS_COLOR.pending} />Pending</span></th>
                  <th className="px-3 py-2.5 text-right"><span className="inline-flex items-center gap-1.5"><Dot color={STATUS_COLOR.converted} />Converted</span></th>
                  <th className="px-3 py-2.5 text-right"><span className="inline-flex items-center gap-1.5"><Dot color={STATUS_COLOR.lost} />Lost</span></th>
                  <th className="px-5 py-2.5 text-right">Conv. rate</th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {cats.map((c) => {
                  // Zeros fade so the categories with activity stand out.
                  const cell = (v: number, color = 'var(--text-primary)') => ({ color: v ? color : 'var(--text-muted)', fontWeight: v ? 700 : 400 });
                  return (
                    <tr key={c.category} className="border-b border-[var(--border-color)] last:border-b-0 hover:bg-[var(--bg-secondary)]">
                      <td className="px-5 py-3 font-semibold text-[var(--text-primary)]">{c.category}</td>
                      <td className="px-3 py-3 text-right" style={cell(c.total)}>{c.total}</td>
                      <td className="px-3 py-3 text-right" style={cell(c.pending, STATUS_COLOR.pending)}>{c.pending}</td>
                      <td className="px-3 py-3 text-right" style={cell(c.converted, STATUS_COLOR.converted)}>{c.converted}</td>
                      <td className="px-3 py-3 text-right" style={cell(c.lost, STATUS_COLOR.lost)}>{c.lost}</td>
                      <td className="px-5 py-3 text-right" style={cell(c.rate, STATUS_COLOR.converted)}>{c.rate}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel title="Leads handled by team member">
        {team.length === 0 ? <Empty>No team members yet — add them in the Team tab</Empty> : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {team.map((p) => (
              <div key={p.name} className="rounded-lg border border-[var(--border-color)] p-3.5">
                <div className="flex items-center gap-2.5">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent/10 text-xs font-extrabold text-accent">
                    {p.name.split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-bold text-[var(--text-primary)]">{p.name}</div>
                    <div className="text-[11px] text-[var(--text-muted)]">{p.inquiries} lead{p.inquiries === 1 ? '' : 's'}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-extrabold tabular-nums" style={{ color: p.rate ? STATUS_COLOR.converted : 'var(--text-muted)' }}>{p.rate}%</div>
                    <div className="text-[10px] text-[var(--text-muted)]">conv. rate</div>
                  </div>
                </div>
                <div className="mt-3"><StatusBar pending={p.pending} converted={p.converted} lost={p.inquiries - p.pending - p.converted} height={6} /></div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-[11px]">
                  <div><div className="text-[var(--text-muted)]">Pending</div><div className="font-bold tabular-nums" style={{ color: p.pending ? STATUS_COLOR.pending : 'var(--text-muted)' }}>{p.pending}</div></div>
                  <div><div className="text-[var(--text-muted)]">Converted</div><div className="font-bold tabular-nums" style={{ color: p.converted ? STATUS_COLOR.converted : 'var(--text-muted)' }}>{p.converted}</div></div>
                  <div className="text-right"><div className="text-[var(--text-muted)]">Value closed</div><div className="font-bold tabular-nums text-[var(--text-primary)]">₹{formatINR(p.valueClosed) || 0}</div></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

// ---------- Leads ----------
/** "2026-09-29" → "29 Sep" (this year) or "29 Sep 25" (other years) for the leads table. */
function fmtDay(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '');
  if (!m) return iso;
  const year = Number(m[1]) === new Date().getFullYear() ? '' : ` ${m[1].slice(2)}`;
  return `${Number(m[3])} ${MONTH_SHORT[Number(m[2]) - 1]}${year}`;
}
/** "2026-09-29" → "29 Sep 2026" — the Date column always shows the full year. */
function fmtFullDay(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '');
  return m ? `${Number(m[3])} ${MONTH_SHORT[Number(m[2]) - 1]} ${m[1]}` : iso;
}
interface LeadsTabProps {
  leads: Lead[];
  search: string; setSearch: (v: string) => void;
  catFilter: string; setCatFilter: (v: string) => void;
  srcFilter: string; setSrcFilter: (v: string) => void;
  statusFilter: string; setStatusFilter: (v: string) => void;
  onNew: () => void; onView: (l: Lead) => void; onEdit: (l: Lead) => void; onDelete: (id: string) => void; onLog: (l: Lead) => void; onConvert: (l: Lead) => void; onExport: () => void; onImport: () => void;
}
function LeadsTab(p: LeadsTabProps) {
  // Apply search here (not via the Table's search prop) so the count in the
  // table header reflects exactly the rows shown, not the pre-search total.
  const terms = p.search.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const rows = p.leads
    .filter((l) => (p.catFilter === 'All' || l.category === p.catFilter)
      && (p.srcFilter === 'All' || l.source === p.srcFilter)
      && (p.statusFilter === 'All' || l.status === p.statusFilter))
    .filter((l) => {
      if (!terms.length) return true;
      const hay = [l.date, l.customer, l.category, l.source, l.referralName, l.contactNumber, l.email, l.status, l.assignedTo, l.convertedCode]
        .map((v) => String(v ?? '').toLowerCase()).join(' ');
      return terms.every((t) => hay.includes(t));
    })
    .sort((a, b) => b.date.localeCompare(a.date));

  // Client-side pagination over the filtered rows.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  // Snap back to page 1 whenever the filtered set changes.
  useEffect(() => { setPage(1); }, [p.search, p.catFilter, p.srcFilter, p.statusFilter]);
  const total = rows.length;
  const pageRows = rows.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div>
      <FilterBar>
        <FilterField label="Search" className="col-span-2 sm:col-auto sm:min-w-[220px]">
          <div className="relative w-full">
            <Search
              size={11}
              className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5"
              strokeWidth={2.25}
            />
            <Input value={p.search} onChange={(e) => p.setSearch(e.target.value)} placeholder="Search" className="!pl-5 sm:!pl-8" aria-label="Search leads" />
          </div>
        </FilterField>
        <FilterField label="Category">
          <Select value={p.catFilter} onChange={(e) => p.setCatFilter(e.target.value)}>
            <option>All</option>{LEAD_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </Select>
        </FilterField>
        <FilterField label="Source">
          <Select value={p.srcFilter} onChange={(e) => p.setSrcFilter(e.target.value)}>
            <option>All</option>{LEAD_SOURCES.map((s) => <option key={s}>{s}</option>)}
          </Select>
        </FilterField>
        <FilterField label="Status">
          <Select value={p.statusFilter} onChange={(e) => p.setStatusFilter(e.target.value)}>
            <option>All</option>{LEAD_STATUSES.map((s) => <option key={s}>{s}</option>)}
          </Select>
        </FilterField>
        <div className="col-span-2 flex flex-wrap items-end gap-2 sm:col-span-1 sm:ml-auto sm:self-end">
          <Button variant="glass" size="sm" onClick={p.onExport} icon={<Download size={13} />}>Export</Button>
          <Button variant="glass" size="sm" onClick={p.onImport} icon={<Upload size={13} />}>Import</Button>
          <Button variant="primary" size="sm" onClick={p.onNew} icon={<Plus size={13} />}>Log lead</Button>
        </div>
      </FilterBar>

      <Table<Lead>
        title="Leads"
        count={`${total} lead${total !== 1 ? 's' : ''}`}
        rows={pageRows}
        rowKey={(l) => l.id}
        empty="No leads match these filters — log one to get started."
        footer={<Pagination page={page} pageSize={pageSize} total={total} setPage={setPage} setPageSize={setPageSize} />}
        // Compact layout: sampling sits under the order value and the follow-up
        // log is two short lines — so the whole row fits without scrolling
        // sideways. Every field is still shown.
        columns={[
          { key: 'date', header: 'Date', cell: (l) => <span className="tabular-nums" style={{ color: 'var(--text-secondary)' }}>{fmtFullDay(l.date)}</span> },
          {
            key: 'customer', header: 'Customer',
            // Long names wrap (instead of stretching the column) so the row fits.
            cell: (l) => <div className="mx-auto min-w-[120px] max-w-[190px] whitespace-normal py-0.5 font-bold leading-snug" style={{ color: 'var(--text-primary)' }}>{l.customer}</div>,
          },
          {
            key: 'category', header: 'Customer Type',
            // Long types ("Corporate & Projects") wrap inside the tag rather than widening the column.
            cell: (l) => (l.category
              ? <span className="inline-block max-w-[110px] whitespace-normal rounded-full border border-accent/30 bg-accent/10 px-2 py-0.5 text-[11px] font-bold leading-tight text-accent">{l.category}</span>
              : <span style={{ color: 'var(--text-muted)' }}>—</span>),
          },
          {
            key: 'source', header: 'Source',
            cell: (l) => <div>{l.source}{l.source === 'Referral' && l.referralName ? <div className="mx-auto max-w-[84px] whitespace-normal text-[11px] font-medium leading-tight" style={{ color: 'var(--text-muted)' }}>{l.referralName}</div> : null}</div>,
          },
          { key: 'assigned', header: 'Assigned', cell: (l) => (l.assignedTo ? <div className="mx-auto max-w-[96px] whitespace-normal leading-snug">{l.assignedTo}</div> : <span style={{ color: 'var(--text-muted)' }}>—</span>) },
          { key: 'status', header: 'Status', cell: (l) => <Badge tone={STATUS_TONE[l.status]}>{l.status}</Badge> },
          {
            key: 'value', header: 'Value',
            cell: (l) => (
              <div className="tabular-nums">
                <div>{l.valueInr ? `₹${formatINR(l.valueInr)}` : <span style={{ color: 'var(--text-muted)' }}>—</span>}</div>
                {l.samplingCostInr ? <div className="text-[11px] font-medium" style={{ color: 'var(--text-muted)' }}>Sampling ₹{formatINR(l.samplingCostInr)}</div> : null}
              </div>
            ),
          },
          {
            key: 'comm', header: 'Follow-ups', pickerLabel: 'Follow-ups (first → last)',
            cell: (l) => {
              const n = commCount(l);
              return (
                <div className="text-[11px] font-medium leading-4" style={{ color: 'var(--text-muted)' }} title={`First contact ${fmtFullDay(firstContact(l))} · last update ${fmtFullDay(lastUpdate(l))}`}>
                  <div><strong style={{ color: n ? 'var(--text-primary)' : 'var(--text-muted)' }}>{n}</strong> logged</div>
                  <div>{fmtDay(firstContact(l))} → {fmtDay(lastUpdate(l))}</div>
                </div>
              );
            },
          },
          {
            key: 'actions', header: 'Action', alwaysVisible: true,
            cell: (l) => (
              <div className="flex items-center justify-center gap-1 whitespace-nowrap">
                {l.status === 'Converted' && !l.convertedCode && (
                  <button onClick={() => p.onConvert(l)} className="mr-1 inline-flex items-center gap-1 rounded border border-green-500/40 bg-green-500/10 px-2 py-1 text-[11px] font-semibold text-green-600 hover:bg-green-500/20" title="Create the customer master entry for this converted lead">
                    <UserCheck size={12} /> Convert
                  </button>
                )}
                {l.convertedCode && (
                  <span className="mr-1 inline-flex items-center gap-1 rounded border border-green-500/30 px-2 py-1 text-[11px] font-semibold text-green-600" title="Customer master entry created">
                    <UserCheck size={12} /> {l.convertedCode}
                  </span>
                )}
                <button onClick={() => p.onLog(l)} className="inline-flex items-center gap-1 rounded-md p-1 text-[var(--text-muted)] hover:bg-accent/10 hover:text-accent xl:border xl:border-[var(--border-color)] xl:px-2 xl:py-1 xl:text-[11px] xl:font-semibold xl:text-[var(--text-secondary)] xl:hover:border-accent/40" title="Log communication"><MessageSquarePlus size={13} /><span className="hidden xl:inline">Log</span></button>
                <button onClick={() => p.onView(l)} className="inline-flex items-center gap-1 rounded-md p-1 text-[var(--text-muted)] hover:bg-accent/10 hover:text-accent xl:border xl:border-[var(--border-color)] xl:px-2 xl:py-1 xl:text-[11px] xl:font-semibold xl:text-[var(--text-secondary)] xl:hover:border-accent/40" title="View details"><Eye size={13} /><span className="hidden xl:inline">View</span></button>
                <button onClick={() => p.onEdit(l)} className="inline-flex items-center gap-1 rounded-md p-1 text-[var(--text-muted)] hover:bg-accent/10 hover:text-accent xl:border xl:border-[var(--border-color)] xl:px-2 xl:py-1 xl:text-[11px] xl:font-semibold xl:text-[var(--text-secondary)] xl:hover:border-accent/40" title="Edit lead"><Pencil size={13} /><span className="hidden xl:inline">Edit</span></button>
                <button onClick={() => p.onDelete(l.id)} className="rounded-md p-1 text-[var(--text-muted)] hover:bg-red-500/10 hover:text-red-500" title="Delete" aria-label="Delete"><Trash2 size={14} /></button>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}

// ---------- Team ----------
const initials = (name: string) => name.split(/\s+/).filter(Boolean).map((w) => w[0]).slice(0, 2).join('').toUpperCase();

function TeamTab({ leads, team, salesStaff, loadingStaff, newPerson, setNewPerson, addPerson, onDelete }: { leads: Lead[]; team: TeamMember[]; salesStaff: { name: string; role: string }[]; loadingStaff: boolean; newPerson: string; setNewPerson: (v: string) => void; addPerson: () => void; onDelete: (id: number) => void }) {
  const people = team.map((t) => t.name);
  const members = team.map((t) => ({
    ...t,
    stats: personStats(leads, t.name),
    // This member's leads within the selected period (leads is already
    // range-filtered), newest first — revealed when the card is expanded.
    leads: leads.filter((l) => l.assignedTo === t.name).sort((a, b) => b.date.localeCompare(a.date)),
  }));
  const [openId, setOpenId] = useState<number | null>(null);
  // Only offer staff who aren't already on the team.
  const available = salesStaff.filter((s) => !people.includes(s.name));
  return (
    <div className="space-y-4">
      <section className="flex flex-wrap items-center gap-3 rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] px-4 py-3">
        <div className="mr-auto">
          <div className="text-sm font-extrabold text-[var(--text-primary)]">Team members</div>
          <div className="text-[11px] text-[var(--text-muted)]">{members.length} on the team · add active Sales Managers / Executives from User Management</div>
        </div>
        <Select
          value={newPerson}
          onChange={(e) => setNewPerson(e.target.value)}
          className="w-full sm:w-72"
          disabled={loadingStaff}
        >
          <option value="">
            {loadingStaff
              ? 'Loading sales staff…'
              : available.length === 0
                ? (salesStaff.length === 0 ? 'No Sales Manager / Executive in User Management' : 'All sales staff already added')
                : '— Select Sales Manager / Executive —'}
          </option>
          {available.map((s) => (
            <option key={s.name} value={s.name}>{s.name} — {s.role}</option>
          ))}
        </Select>
        <Button variant="primary" size="sm" onClick={addPerson} icon={<Plus size={13} />} disabled={!newPerson}>Add member</Button>
      </section>

      {members.length === 0 ? (
        <Panel title="Team"><Empty>No team members yet. Add one above to start assigning leads.</Empty></Panel>
      ) : (
        <div className="grid items-start gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {members.map((m) => {
            const open = openId === m.id;
            const s = m.stats;
            return (
              <div key={m.id} className="rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] p-4">
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent/10 text-sm font-extrabold text-accent">{initials(m.name)}</span>
                  {/* Name — click to reveal this member's leads */}
                  <button type="button" onClick={() => setOpenId(open ? null : m.id)} className="min-w-0 flex-1 text-left" aria-expanded={open} title={open ? 'Hide leads' : 'Show leads'}>
                    <div className="flex items-center gap-1 text-[15px] font-extrabold text-[var(--text-primary)]">
                      <span className="truncate">{m.name}</span>
                      <ChevronDown size={15} className="shrink-0 text-[var(--text-muted)] transition-transform" style={{ transform: open ? 'rotate(180deg)' : 'none' }} />
                    </div>
                    <div className="text-[11px] text-[var(--text-muted)]">{s.inquiries} lead{s.inquiries === 1 ? '' : 's'} handled</div>
                  </button>
                  <div className="text-right">
                    <div className="text-lg font-extrabold leading-none tabular-nums" style={{ color: s.rate ? STATUS_COLOR.converted : 'var(--text-muted)' }}>{s.rate}%</div>
                    <div className="mt-1 text-[10px] text-[var(--text-muted)]">conv. rate</div>
                  </div>
                  <button type="button" onClick={() => onDelete(m.id)} className="-mr-1 rounded-md p-1 text-[var(--text-muted)] hover:bg-red-500/10 hover:text-red-500" title="Remove from team" aria-label="Remove from team">
                    <Trash2 size={15} />
                  </button>
                </div>

                <div className="mt-4"><StatusBar pending={s.pending} converted={s.converted} lost={s.inquiries - s.pending - s.converted} height={6} /></div>

                <div className="mt-4 grid grid-cols-3 divide-x divide-[var(--border-color)] rounded-lg bg-[var(--bg-secondary)] py-2.5 text-center">
                  <TeamFigure label="Pending" value={s.pending} color={STATUS_COLOR.pending} />
                  <TeamFigure label="Converted" value={s.converted} color={STATUS_COLOR.converted} />
                  <TeamFigure label="Value closed" value={s.valueClosed} money />
                </div>

                {open && (
                  <div className="mt-3 border-t border-[var(--border-color)] pt-3">
                    <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
                      Leads this period ({m.leads.length})
                    </div>
                    {m.leads.length === 0 ? (
                      <div className="py-3 text-center text-xs text-[var(--text-muted)]">No leads in this period.</div>
                    ) : (
                      <div className="max-h-56 space-y-1.5 overflow-y-auto pr-1">
                        {m.leads.map((l) => (
                          <div key={l.id} className="rounded-md border border-[var(--border-color)] bg-[var(--bg-soft)] px-2.5 py-1.5">
                            <div className="flex items-center justify-between gap-2">
                              <span className="truncate text-xs font-semibold text-[var(--text-primary)]">{l.customer}</span>
                              <Badge tone={STATUS_TONE[l.status]}>{l.status}</Badge>
                            </div>
                            <div className="mt-0.5 flex items-center justify-between gap-2 text-[11px] text-[var(--text-muted)]">
                              <span className="truncate">{fmtFullDay(l.date)}{l.category ? ` · ${l.category}` : ''}</span>
                              <span className="flex-shrink-0 font-semibold text-accent">{l.valueInr ? `₹${formatINR(l.valueInr)}` : '—'}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
/** One figure in a team card's stats strip; zeros fade to muted. */
function TeamFigure({ label, value, color, money }: { label: string; value: number; color?: string; money?: boolean }) {
  return (
    <div className="min-w-0 px-2">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-muted)]">{label}</div>
      <div className="mt-0.5 truncate text-base font-extrabold tabular-nums" style={{ color: value ? (color ?? 'var(--text-primary)') : 'var(--text-muted)' }}>
        {money ? `₹${formatINR(value) || 0}` : value}
      </div>
    </div>
  );
}

// ---------- Accounts ----------
const ACCOUNTS_PAGE_SIZE = 20;
function AccountsTab({ leads }: { leads: Lead[] }) {
  const allRows = useMemo(() => accountsRollup(leads), [leads]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  // Search the full list, then paginate the results (Table's own search would
  // only filter the current 20-row page).
  const q = search.trim().toLowerCase();
  const all = useMemo(
    () => (q ? allRows.filter((a) => a.name.toLowerCase().includes(q)) : allRows),
    [allRows, q],
  );
  const totalPages = Math.max(1, Math.ceil(all.length / ACCOUNTS_PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const rows = all.slice((current - 1) * ACCOUNTS_PAGE_SIZE, current * ACCOUNTS_PAGE_SIZE);
  return (
    <div className="space-y-3">
      <div className="relative w-full sm:max-w-xs">
        <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
        <Input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search accounts…"
          aria-label="Search accounts"
          className="!pl-9"
        />
      </div>
      <Table<AccountRow>
        title="Accounts — grouped by customer & customer type"
        count={`${all.length} account${all.length !== 1 ? 's' : ''}`}
        rows={rows}
        rowKey={(a) => a.name}
        empty={q ? 'No accounts match your search' : 'No accounts in this period'}
      columns={[
        { key: 'name', header: 'Customer', cell: (a) => <span className="font-bold">{a.customer}</span>, minWidth: '220px' },
        { key: 'category', header: 'Customer Type', cell: (a) => (a.category ? <Badge tone="accent">{a.category}</Badge> : <span style={{ color: 'var(--text-muted)' }}>—</span>) },
        { key: 'leads', header: 'Leads', cell: (a) => a.inquiries },
        { key: 'converted', header: 'Converted', cell: (a) => <span style={{ color: '#22c55e' }}>{a.converted}</span> },
        { key: 'value', header: 'Order Value', cell: (a) => formatINR(a.value) },
      ]}
      footer={
        all.length > ACCOUNTS_PAGE_SIZE ? (
          <div className="flex items-center justify-between">
            <span>
              Showing {(current - 1) * ACCOUNTS_PAGE_SIZE + 1}–{Math.min(current * ACCOUNTS_PAGE_SIZE, all.length)} of {all.length}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="glass" size="sm" disabled={current <= 1} onClick={() => setPage(current - 1)}>Prev</Button>
              <span>Page {current} / {totalPages}</span>
              <Button variant="glass" size="sm" disabled={current >= totalPages} onClick={() => setPage(current + 1)}>Next</Button>
            </div>
          </div>
        ) : undefined
      }
      />
    </div>
  );
}
